import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, isDevMode } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { filter, switchMap, take, takeUntil } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { ConfirmationDialogService } from '../../../components/confirmation-dialog/confirmation-dialog.service';
import { SaveAsCalculationComponent } from '../../../components/save-as-calculation/save-as-calculation.component';
import { WatermarkComponent } from '../../../components/watermark/watermark.component';
import { ContainerRefDirective } from '../../../shared/directives/container-ref.directive';
import { AccessTypeEnum } from '../../../shared/enums/access-type.enum';
import { DataSetSettingEnum } from "../../../shared/enums/data-set-setting.enum";
import { HttpSuccessStatusCode } from '../../../shared/enums/http-request-status-code.enum';
import { IndicatorsEnum } from '../../../shared/enums/indicator.enum';
import { MpgSettingEnum } from '../../../shared/enums/mpg-setting.enum';
import { DialogDataModel } from '../../../shared/models/dialog-data.interface';
import { GprMaterialsV1Service } from '../../services/GPRMV1Service.Generated';
import { AppService } from '../../services/app.service';
import { AuthService } from '../../services/auth.service';
import { FeatureManagementService } from '../../services/feature-management.service';
import { StatusCheckService } from '../../services/status-check.service';
import { ToasterService } from '../../services/toaster/toaster.service';


@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    standalone: false
})
export class HeaderComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild(ContainerRefDirective, { static: true }) appContainerRef!: ContainerRefDirective;

    isPrinting = false;
    urlId: string;
    mpg: number;
    mpgAfter: number;
    ppi: number;
    ppiAfter: number;
    userPortalBaseUrl: string = environment.gprUserPortalBaseUrl;
    calculationStatus: Observable<number>;
    calculationAccess$: Observable<boolean>;
    indicatorsEnum = IndicatorsEnum;
    mpgSettingEnum = MpgSettingEnum;
    mpgSetting: MpgSettingEnum = parseInt(localStorage.getItem('mpgSetting')); // Selected MPG settings
    selectedIndicator: IndicatorsEnum = parseInt(localStorage.getItem('indicatorSetting'));

    isExistingBuildingSelected: boolean;
    isDemoLicense: boolean;
    datasetChoiceId: DataSetSettingEnum;
    dataSetSetting = DataSetSettingEnum;
    containerId: number;
    isShowIndicatorPage: boolean = false;
    isShowMaterialPassportPage: boolean = false;
    isGprGebouwCalculation: boolean = false;
    isProjectPhasePurposeStatusDataAdded: boolean = true;
    isProjectPhaseUIEnabled = false;
    isReadOnly: boolean = false;
    showReadonlyBanner = true;
    isDevMode = isDevMode()

    /** Subject that emits when the component has been destroyed. */
    protected _onDestroy: Subject<void> = new Subject<void>();
    private _subscribedObservable = new Subject();
    constructor(
        private _appService: AppService,
        private _router: Router,
        private _authService: AuthService,
        private _statusService: StatusCheckService,
        private _featureService: FeatureManagementService,
        private _confirmDialogService: ConfirmationDialogService,
        private _translate: TranslateService,
        private _dialog: MatDialog,
        private _gprMaterialService: GprMaterialsV1Service,
        private _toasterService: ToasterService
    ) {
        this.calculationStatus = _appService.calculationStatus$;
        this.calculationAccess$ = _appService.calculationAccess$;
    }

    ngOnInit() {
        this.urlId = localStorage.getItem('urlId');

        this._appService.getIsExistingBuildingCalculation$().pipe(takeUntil(this._onDestroy))
            .subscribe(resp => this.isExistingBuildingSelected = resp);

        this._appService.getMpgSetting().pipe(takeUntil(this._onDestroy))
            .subscribe((mpgSettingEnum) => this.mpgSetting = mpgSettingEnum);

        this._appService.getIndicatorsSetting().pipe(takeUntil(this._onDestroy))
            .subscribe((mpgIndicatorsEnum) => this.selectedIndicator = mpgIndicatorsEnum);

        this.callMpgs();
        this.getShowIndicatorPageFeature();
        this.getShowMaterialPassportPageFeature();

        this._appService.accessTypeId$
            .pipe(
                filter(id => id !== null),
                take(1),
                switchMap(accessTypeId =>
                    forkJoin([
                        this._featureService.isProjectPhaseUIEnabled(),
                        of(accessTypeId === AccessTypeEnum.ReadOnly)
                    ])
                ),
                takeUntil(this._subscribedObservable)
            )
            .subscribe(([projectPhaseUIEnable, isReadOnly]) => {
                this.isProjectPhaseUIEnabled = projectPhaseUIEnable;
                this.isReadOnly = isReadOnly;
                // Skipping check for ProjectPhase UI when ReadOnly mode and data is not set to ProjectPhase
                if (!this.isProjectPhaseUIEnabled || this.isReadOnly) {
                    this.isProjectPhasePurposeStatusDataAdded = true;
                } else {
                    this._appService.getIsProjectPhasePurposeStatusDataAdded()
                        .pipe(takeUntil(this._onDestroy))
                        .subscribe(resp => {
                            this.isProjectPhasePurposeStatusDataAdded = resp;
                        });
                }
            });
    }

    ngAfterViewInit() {
        this._appService.getIsDemoLicense().pipe(takeUntil(this._onDestroy))
            .subscribe(isDemo => {
                this.isDemoLicense = isDemo;
                if (isDemo) {
                    this.loadWatermarkComponent();
                }
            });
    }

    /**
     * It's load the demo watermark component to the html container 
     */
    loadWatermarkComponent() {
        const viewContainerRef = this.appContainerRef.viewContainerRef;
        viewContainerRef.clear();

        const componentRef = viewContainerRef.createComponent<WatermarkComponent>(WatermarkComponent);
        componentRef.instance.content = 'DEMO';
        componentRef.instance.count = 1;
        componentRef.instance.className = 'header_watermark';
    }

    callMpgs() {
        this._appService.getDatasetChoiceValue().pipe(takeUntil(this._onDestroy)).subscribe(dataSet => {
            this.datasetChoiceId = dataSet;
        });
        this._appService.getMpgValue().pipe(takeUntil(this._onDestroy)).subscribe(mpgCurrent => this.mpg = mpgCurrent);
        this._appService.getMpgAfterValue().pipe(takeUntil(this._onDestroy)).subscribe(mpgAfter => this.mpgAfter = mpgAfter);

        this._appService.getPPIValue().pipe(takeUntil(this._onDestroy)).subscribe(ppiCurrent => this.ppi = ppiCurrent);
        this._appService.getPPIAfterValue().pipe(takeUntil(this._onDestroy)).subscribe(ppiAfter => {
            var isGprGebouwCalculation = this._appService.getIsGprGebouwCalculation();
            this.ppiAfter = ppiAfter;
            this.isGprGebouwCalculation = isGprGebouwCalculation;
        });
    }

    logout($event: Event) {
        const status = this._statusService.getMainElementStatus();

        if (status && !this.hasUnsavedChanges()) {
            this._authService.logout();
            return;
        }
        $event.preventDefault();
        this._statusService.openConfirmation()
            .pipe(takeUntil(this._onDestroy))
            .subscribe((event) => {
                if (event) {
                    this.setAllUnsavedFlagFalse();
                    this._authService.logout();
                }
            });
    }

    printPage(selection?: string, isPreview?: boolean): void {
        if (!this.hasUnsavedChanges()) {
            return this.navigateToPrintPage(selection, isPreview);
        }

        this._statusService.openConfirmation()
            .pipe(takeUntil(this._onDestroy))
            .subscribe((event) => {
                if (event) {
                    this.setAllUnsavedFlagFalse();
                    return this.navigateToPrintPage(selection, isPreview);
                }
            });
    }

    navigateToPrintPage(selection?: string, isPreview?: boolean): void {
        if (this.isDemoLicense) {
            this.popupDemoModeMessage();
            return;
        }

        this.isPrinting = true;
        const queryParams: any = selection ? { type: selection } : {};
        if (isPreview) {
            queryParams.preview = true;
        }
        // If preview is requested open the print page directly as primary route in a new tab/window.
        // Otherwise open the print outlet on the current page.
        if (isPreview) {
            const urlId = this.urlId || this._appService.getCalculationUrlId();
            const primarySegments = urlId ? ['/', urlId, 'print'] : ['/', 'print'];

            const urlTree = this._router.createUrlTree(primarySegments, { queryParams });
            const serialized = this._router.serializeUrl(urlTree);
            const base = environment.clientRoot.endsWith('/') ? environment.clientRoot : environment.clientRoot + '/';
            const fullUrl = base + serialized.replace(/^\//, '');

            window.open(fullUrl, '_blank');
            return;
        }

        // non-preview: open as auxiliary outlet on current primary route
        this._router.navigate(['/', { outlets: { print: ['print'] } }], { queryParams });
    }

    exportCalculation() {
        if (!this.hasUnsavedChanges()) {
            if (this.isDemoLicense) {
                this.popupDemoModeMessage();
                return;
            }

            this._appService.getExportCalculation()
                .pipe(takeUntil(this._onDestroy)).subscribe();
            return;
        }

        this._statusService.openConfirmation()
            .pipe(takeUntil(this._onDestroy))
            .subscribe((event) => {
                if (event) {
                    if (this.isDemoLicense) {
                        this.popupDemoModeMessage();
                        return;
                    }

                    this._appService.getExportCalculation()
                        .pipe(takeUntil(this._onDestroy)).subscribe();

                    this.setAllUnsavedFlagFalse();
                }
            });
    }

    /**
     * disposes the resource in the subscription
     */
    ngOnDestroy() {
        this._onDestroy.next();
        this._onDestroy.complete();
        this._subscribedObservable.next(null);  // triggers all takeUntil
        this._subscribedObservable.complete();
    }

    checkChangeStatus($event: { preventDefault: () => void; }, element: { _elementRef: { nativeElement: { click: () => void; }; }; }) {
        if (!this.hasUnsavedChanges()) {
            return;
        }

        $event.preventDefault();
        this._statusService.openConfirmation()
            .pipe(takeUntil(this._onDestroy))
            .subscribe((event) => {
                if (event) {
                    this.setAllUnsavedFlagFalse();
                    element._elementRef.nativeElement.click();
                }
            });
    }

    /**
     * Open popup for demo mode
     */
    popupDemoModeMessage() {
        const dialogData: DialogDataModel = {
            title: this._translate.instant('MESSAGES.ACCESS_RESTRICTED'),
            message: this._translate.instant('MESSAGES.DEMO_MODE_FUNCTION_UNAVAILABLE'),
            hasCloseBtn: true,
            hasContinueBtn: false
        }

        this._confirmDialogService.openDialog(dialogData);
    }

    saveAsCalculation() {
        var calculation = this._appService.getCalculation();
        this.containerId = calculation.containerId;
        if (!this.hasUnsavedChanges()) {
            this.openModalForSaveAsCalculation();
            return;
        }

        this._statusService.openConfirmation()
            .pipe(takeUntil(this._onDestroy))
            .subscribe((event) => {
                if (event) {
                    this.setAllUnsavedFlagFalse();
                    this.openModalForSaveAsCalculation();
                }
            });
    }

    /**
   * Opens a modal for save as calculation
   */
    openModalForSaveAsCalculation() {
        const dialogRef = this._dialog.open(SaveAsCalculationComponent, {
            disableClose: true,
            width: "400px",
        });

        dialogRef.componentInstance.submitData$.subscribe(
            (calculationName: string) => {
                this._gprMaterialService.saveAsCalculation(this.urlId, this.containerId, calculationName)
                    .pipe(takeUntil(this._subscribedObservable))
                    .subscribe(
                        (resp: string) => {
                            this._toasterService.successMethod({
                                statusCode: HttpSuccessStatusCode.CREATED,
                                message: this._translate.instant(
                                    "VALIDATION_MESSAGE.SUCCESSFULLY_CREATED"
                                ),
                            });

                            this.openCalculation(resp);
                        },
                        (err) => this._toasterService.errorMethod(err)
                    );

                dialogRef.close();
            }
        );
    }

    openCalculation(urlId: string) {
        window.open(`${environment.clientRoot.endsWith('/') ? environment.clientRoot : environment.clientRoot + '/'}${urlId}`, "_self");
    }

    /** 
     * sets all flags false when user clicks on confirm
    */
    setAllUnsavedFlagFalse() {
        sessionStorage.setItem('isBuildingDataUnsaved', 'false');
        sessionStorage.setItem('isElementDataUnsaved', 'false');
        sessionStorage.setItem('isProjectDataUnsaved', 'false');
        sessionStorage.setItem('isExtraIndicatorDataUnsaved', 'false');
    }

    hasUnsavedChanges(): boolean {
        let isBuildingDataUnsaved = (sessionStorage.getItem('isBuildingDataUnsaved') === 'true');
        let isElementDataUnsaved = (sessionStorage.getItem('isElementDataUnsaved') === 'true');
        let isProjectDataUnsaved = (sessionStorage.getItem('isProjectDataUnsaved') === 'true');
        let isExtraIndicatorDataUnsaved = (sessionStorage.getItem('isExtraIndicatorDataUnsaved') === 'true');
        if (isBuildingDataUnsaved || isElementDataUnsaved || isProjectDataUnsaved || isExtraIndicatorDataUnsaved) {
            return true;
        } else {
            return false;;
        }
    }

    /**
     * get the ShowIndicatorPage feature flag
    */
    getShowIndicatorPageFeature() {
        this._featureService.isShowIndicatorPage()
            .pipe(takeUntil(this._onDestroy))
            .subscribe(resp => this.isShowIndicatorPage = resp);
    }

    /**
     * get the ShowMaterialPassportPage feature flag
    */
    getShowMaterialPassportPageFeature() {
        this._featureService.isShowMaterialPassportPage()
            .pipe(takeUntil(this._onDestroy))
            .subscribe(resp => this.isShowMaterialPassportPage = resp);
    }
}
